/**
 * Comparación de flota — ordena vehículos entre sí sin mentir en el orden.
 *
 * Implementa CAP-02. Es una función pura: mismas filas, misma tabla, en
 * cualquier orden en que lleguen.
 *
 * La tentación evidente es meter los treinta vehículos en una tabla y
 * ordenarlos por episodios. Eso produce un ranking que se lee solo y que está
 * mal por tres motivos distintos, y los tres se corrigen aquí:
 *
 *  1. **Contar episodios premia al que manejó poco.** La cifra comparable es la
 *     tasa por 100 km. Se usa por 100 km y no por hora porque el equipo sigue
 *     reportando con el vehículo parqueado: el tiempo cubierto incluye
 *     estacionamiento y una tasa por hora mide, sobre todo, dónde durmió el
 *     carro.
 *
 *  2. **Dos vehículos con perfiles distintos no son comparables.** Uno que
 *     reporta cada 5 s y otro cada 10 s no están midiendo la misma magnitud
 *     (ver `pipeline/profiles.ts`). Ponerlos en la misma columna es comparar
 *     picos con promedios. Se agrupan por perfil y solo se ordena dentro del
 *     grupo.
 *
 *  3. **Un cero puede ser «no hubo» o «no medimos».** Cuando la cadencia deja
 *     ciegos a los detectores (ADR-007), el vehículo no entra al ranking: sale
 *     aparte, marcado, porque un cero ciego lo pondría primero en la lista de
 *     los mejores conductores.
 *
 * Ninguna de las tres es una decisión estética. Cada una es la diferencia entre
 * una tabla que se puede defender frente a un cliente y una que no.
 *
 * ## La cuarta, que faltaba: el cero mudo que la cadencia no explica
 *
 * La tercera regla miraba SOLO la cadencia (`detectorsBlindedBySampling`), y con
 * eso se le escapaba el caso más común de producción: un equipo que reporta cada
 * 10 s —cadencia de sobra para el umbral— cuyo umbral de aceleración queda por
 * encima de lo más alto que ese vehículo hizo en todo el período. Ese vehículo
 * entraba al ranking con dos familias mudas, su tasa por 100 km se calculaba como
 * si hubiera medido las cuatro, y competía contra vehículos que sí midieron todo
 * — ganando por no haber podido medir. Es el mismo defecto que
 * `report/component-measurability.ts` cerró en el informe de un vehículo,
 * entrando por la vista de flota.
 *
 * Por eso la fila trae ahora la medibilidad por familia y no solo el booleano de
 * cadencia: quien decide si una familia se pudo medir es el servidor, una vez,
 * con el perfil aplicado y las mediciones del período delante.
 */

import {
  explainUnmeasured,
  type FamilyMeasurability,
} from "../report/component-measurability.ts";
import { COMPONENT_LABELS } from "../report/drivescore-v1.ts";

export const FLEET_COMPARISON_VERSION = "fleet-v1" as const;

/** Lo que el servidor mide de cada vehículo. Deliberadamente chico: la flota entera cabe en pantalla. */
export type FleetVehicleRow = {
  deviceId: string;
  plate: string | null;
  name: string | null;
  group: string | null;
  status: "VALID" | "PARTIAL" | "INSUFFICIENT_DATA";
  distanceKm: number;
  coveredHours: number;
  /**
   * Episodios CONSOLIDADOS por familia, los mismos que publica el informe del
   * vehículo. No candidatos: ver `fleetRow` en `server/index.ts`, donde se
   * explica por qué la magnitud es esta y no la otra.
   *
   * Las cuatro familias suman `total`: cada episodio pertenece a una sola
   * (`consolidate` solo fusiona candidatos del mismo tipo).
   */
  episodes: {
    acceleration: number;
    braking: number;
    turn: number;
    elevatedSpeed: number;
    total: number;
  };
  severity: { moderate: number; high: number; critical: number };
  /** Calidad de los registros. La cohorte pondera por ella. */
  validPercent: number | null;
  /**
   * Atributos de riesgo del vehículo, ya resueltos en el servidor sobre los
   * vehículos que este usuario puede ver. Van aquí y no en un endpoint aparte
   * porque la autorización ya se hizo: una segunda puerta sería una segunda
   * oportunidad de equivocarse.
   */
  attributes: Record<string, string | null>;
  medianIntervalSeconds: number | null;
  regime: "HIGH" | "MEDIUM" | "LOW" | "UNKNOWN";
  detectorsBlindedBySampling: boolean;
  /**
   * Medibilidad por familia, decidida en el servidor con el perfil aplicado.
   *
   * Opcional a propósito, igual que en `analysisResponseSchema`: una pestaña
   * abierta desde antes del despliegue recibe filas sin este campo, y un
   * contrato que las rechazara convertiría una mejora del ranking en una
   * pantalla en blanco. La ausencia se trata explícitamente en `excludeReason`
   * —se cae al criterio anterior, solo cadencia—, no con un `?.` escondido.
   */
  measurability?: FamilyMeasurability[];
  profileId: string;
  calibration: "CALIBRATED" | "UNCALIBRATED";
  analysisSignature: string;
};

/** Vehículo que sí entra al ranking, ya con sus tasas. */
export type RankedVehicle = FleetVehicleRow & {
  /** Episodios por cada 100 km. `null` cuando no hay distancia que dividir. */
  episodesPer100Km: number | null;
  accelerationPer100Km: number | null;
  brakingPer100Km: number | null;
  turnPer100Km: number | null;
  elevatedSpeedPer100Km: number | null;
  /** Posición dentro de su grupo de perfil, 1 es el de tasa más baja. */
  rank: number;
  /** Velocidad media del período: delata cuánto del tiempo fue estacionamiento. */
  averageSpeedKmh: number | null;
};

export type ExcludedReason =
  | "DETECTORS_BLINDED"
  /** Alguna familia no se pudo medir en el período, y no fue por la cadencia. */
  | "UNMEASURED_FAMILIES"
  | "INSUFFICIENT_DATA"
  | "NO_DISTANCE"
  | "BELOW_MINIMUM_DISTANCE";

export type ExcludedVehicle = FleetVehicleRow & {
  reason: ExcludedReason;
  /** Por qué quedó fuera, en palabras, para que salga en pantalla. */
  explanation: string;
};

export type ProfileGroup = {
  profileId: string;
  regime: "HIGH" | "MEDIUM" | "LOW" | "UNKNOWN";
  calibration: "CALIBRATED" | "UNCALIBRATED";
  /** Qué mide este grupo, y por qué no se compara con los otros. */
  comparability: string;
  vehicles: RankedVehicle[];
  /** Tasa agregada del grupo: Σ episodios ÷ Σ km × 100. No es el promedio de las tasas. */
  groupEpisodesPer100Km: number | null;
  groupDistanceKm: number;
};

export type FleetComparison = {
  version: typeof FLEET_COMPARISON_VERSION;
  /** Distancia mínima para entrar al ranking. Debajo, la tasa es ruido. */
  minimumDistanceKm: number;
  analysed: number;
  ranked: number;
  groups: ProfileGroup[];
  excluded: ExcludedVehicle[];
  /** Advertencias de lectura de toda la tabla, ya redactadas. */
  warnings: string[];
};

/**
 * Distancia mínima para publicar una tasa por 100 km.
 *
 * Con 4 km recorridos, un solo episodio da 25 por 100 km y encabeza la tabla de
 * los peores. No es que maneje mal: es que dividimos por casi nada.
 */
export const MINIMUM_DISTANCE_KM = 20;

const round = (value: number) => Math.round(value * 100) / 100;

const per100Km = (count: number, distanceKm: number): number | null =>
  distanceKm > 0 ? round((count / distanceKm) * 100) : null;

/**
 * Orden estable y total: por tasa ascendente, y ante empate por placa y luego
 * por `deviceId`. Sin el desempate, dos corridas de los mismos datos podrían
 * devolver órdenes distintos y la tabla dejaría de ser reproducible.
 */
function compareVehicles(a: RankedVehicle, b: RankedVehicle): number {
  const rateA = a.episodesPer100Km ?? Number.POSITIVE_INFINITY;
  const rateB = b.episodesPer100Km ?? Number.POSITIVE_INFINITY;
  if (rateA !== rateB) return rateA - rateB;
  const plateA = a.plate ?? "";
  const plateB = b.plate ?? "";
  if (plateA !== plateB) return plateA < plateB ? -1 : 1;
  return a.deviceId < b.deviceId ? -1 : a.deviceId > b.deviceId ? 1 : 0;
}

function excludeReason(row: FleetVehicleRow, minimumDistanceKm: number): ExcludedVehicle | null {
  if (row.status === "INSUFFICIENT_DATA") {
    return {
      ...row,
      reason: "INSUFFICIENT_DATA",
      explanation:
        "El equipo no entregó suficientes posiciones válidas en el período. No es una conducción sin eventos: es un período sin datos.",
    };
  }
  // La cadencia va primero porque es la causa que NO depende del perfil: se
  // corrige configurando el equipo, y el aviso tiene que decir eso y no mandar a
  // recalibrar. La alternativa era un solo motivo que abarcara las dos causas,
  // redactado por familia; se descartó porque perdía esa distinción —quién tiene
  // que arreglarlo es distinto en cada caso— y porque cambiaba el motivo de los
  // vehículos que hoy ya salen excluidos, sin que eso arregle nada.
  if (row.detectorsBlindedBySampling) {
    return {
      ...row,
      reason: "DETECTORS_BLINDED",
      explanation:
        `En marcha, el equipo reporta cada ${row.medianIntervalSeconds ?? "?"} s y a esa cadencia los detectores de este perfil no alcanzan a dispararse. ` +
        "Sus ceros significan «no medimos», no «no ocurrió», y en un ranking lo pondrían de primero.",
    };
  }
  if (row.distanceKm <= 0) {
    return {
      ...row,
      reason: "NO_DISTANCE",
      explanation: "No recorrió distancia elegible en el período: no hay por cuánto dividir.",
    };
  }
  if (row.distanceKm < minimumDistanceKm) {
    return {
      ...row,
      reason: "BELOW_MINIMUM_DISTANCE",
      explanation:
        `Solo ${row.distanceKm} km recorridos, por debajo de los ${minimumDistanceKm} km mínimos. ` +
        "Con tan poca distancia un episodio suelto dispara la tasa y la tabla mostraría ruido como si fuera conducta.",
    };
  }
  // Familias mudas que la cadencia no explica: el umbral quedó por encima de todo
  // lo que este vehículo hizo en el período. La tasa por 100 km de la fila se
  // calcula sobre las cuatro familias, así que dejarlo competir es dejarlo ganar
  // por lo que no pudo medir. Sin el campo —fila de un servidor anterior— se cae
  // al criterio de la cadencia, que es el que había.
  //
  // Va DESPUÉS de los mínimos de distancia y no antes: con 4 km recorridos no hay
  // tasa que publicar de ninguna manera, y el aviso de este motivo manda a
  // calibrar el vehículo contra su propia distribución — que sobre un período así
  // sería mandar a calibrar contra una muestra que no alcanza para nada.
  const unmeasured = row.measurability?.filter((family) => !family.measured) ?? [];
  if (unmeasured.length) {
    return {
      ...row,
      reason: "UNMEASURED_FAMILIES",
      explanation:
        `${unmeasured.length} de ${row.measurability!.length} familias no se pudieron medir en el período, y no fue por la cadencia. ` +
        // Las líneas por familia salen de la MISMA función que redacta el aviso
        // del informe: dos redacciones del mismo hecho acabarían diciendo cosas
        // distintas del mismo vehículo el mismo día.
        explainUnmeasured(unmeasured, COMPONENT_LABELS).join(" ") +
        " Su tasa por 100 km no es comparable con la de un vehículo que midió las cuatro.",
    };
  }
  return null;
}

function toRanked(row: FleetVehicleRow): RankedVehicle {
  return {
    ...row,
    episodesPer100Km: per100Km(row.episodes.total, row.distanceKm),
    accelerationPer100Km: per100Km(row.episodes.acceleration, row.distanceKm),
    brakingPer100Km: per100Km(row.episodes.braking, row.distanceKm),
    turnPer100Km: per100Km(row.episodes.turn, row.distanceKm),
    elevatedSpeedPer100Km: per100Km(row.episodes.elevatedSpeed, row.distanceKm),
    averageSpeedKmh: row.coveredHours > 0 ? round(row.distanceKm / row.coveredHours) : null,
    rank: 0,
  };
}

export function buildFleetComparison(input: {
  rows: FleetVehicleRow[];
  minimumDistanceKm?: number;
}): FleetComparison {
  const minimumDistanceKm = input.minimumDistanceKm ?? MINIMUM_DISTANCE_KM;

  const excluded: ExcludedVehicle[] = [];
  const eligible: RankedVehicle[] = [];

  for (const row of input.rows) {
    const reason = excludeReason(row, minimumDistanceKm);
    if (reason) excluded.push(reason);
    else eligible.push(toRanked(row));
  }

  // Un grupo por perfil aplicado. Comparar entre grupos sería comparar el pico
  // de un evento contra su intensidad promedio: números distintos con el mismo
  // nombre.
  const byProfile = new Map<string, RankedVehicle[]>();
  for (const vehicle of eligible) {
    const bucket = byProfile.get(vehicle.profileId);
    if (bucket) bucket.push(vehicle);
    else byProfile.set(vehicle.profileId, [vehicle]);
  }

  /*
   * Cuántos perfiles distintos hay POR CADENCIA.
   *
   * Es lo que separa las dos causas de que la tabla se parta en varios grupos, y
   * sin ese número las dos se confunden. Hasta que existió el alcance por
   * vehículo, un grupo por perfil era un grupo por cadencia y decir «los equipos
   * no reportan a la misma cadencia» era cierto por construcción. Desde que
   * `scopedProfileId` le pone el vehículo al `profileId`, siete equipos de 10 s
   * calibrados uno a uno producen siete perfiles a la MISMA cadencia — y el aviso
   * viejo manda a revisar la configuración de siete equipos que están bien.
   *
   * Se cuenta sobre el régimen y no sobre la forma del `profileId` a propósito:
   * el formato del id lo decide `server/calibration/profile-store.ts`, y `core/`
   * no puede importar de `server/` (constitución, Art. 8). Partir el id por «@»
   * aquí sería una copia del formato esperando a divergir; el régimen es un dato
   * de la fila, que es lo que este motor sí tiene derecho a mirar.
   */
  const profilesByRegime = new Map<string, number>();
  for (const vehicles of byProfile.values()) {
    const regime = vehicles[0]!.regime;
    profilesByRegime.set(regime, (profilesByRegime.get(regime) ?? 0) + 1);
  }
  /** Hay más grupos que cadencias: alguna cadencia trae perfiles por vehículo. */
  const splitByScope = byProfile.size > profilesByRegime.size;
  /** Hay más de una cadencia: esa sí es la causa que el aviso viejo nombraba. */
  const splitByCadence = profilesByRegime.size > 1;

  const groups: ProfileGroup[] = [...byProfile.entries()]
    // Orden fijo por nombre de perfil: la tabla tiene que ser igual entre corridas.
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([profileId, vehicles]) => {
      const sorted = [...vehicles].sort(compareVehicles);
      sorted.forEach((vehicle, index) => {
        vehicle.rank = index + 1;
      });
      const first = sorted[0]!;
      const totalEpisodes = sorted.reduce((sum, v) => sum + v.episodes.total, 0);
      const totalKm = sorted.reduce((sum, v) => sum + v.distanceKm, 0);
      /*
       * Por qué este grupo no se compara con los otros, dicho con la causa que
       * de verdad lo separa de ellos. Las dos pueden darse a la vez —tres grupos
       * a 10 s con perfil propio más uno a 5 s— y entonces se nombran las dos.
       */
      const compartenCadencia = (profilesByRegime.get(first.regime) ?? 1) > 1;
      const hayOtraCadencia = profilesByRegime.size > 1;
      const noComparable = !compartenCadencia && !hayOtraCadencia
        ? // Un solo grupo: no hay con qué no compararse, y afirmarlo igual
          // enseñaba a leer la frase como decorado.
          ""
        : compartenCadencia && hayOtraCadencia
          ? "No se comparan con los de otro perfil, y por dos razones distintas: a distinta cadencia se mide una magnitud distinta, y un perfil de alcance por vehículo mide contra la distribución de ESE vehículo, no contra la de los demás."
          : compartenCadencia
            ? "No se comparan con los de otro perfil: todos los perfiles de esta tabla son de la misma cadencia y lo que cambia es el alcance —hay vehículos calibrados contra su propia distribución—, así que un episodio no cuenta lo mismo en dos de ellos. No es un problema de configuración de los equipos."
            : "No se comparan con los de otro perfil: a distinta cadencia se mide una magnitud distinta.";
      return {
        profileId,
        regime: first.regime,
        calibration: first.calibration,
        comparability: (
          `Vehículos analizados con el perfil ${profileId}. ` +
          (first.calibration === "UNCALIBRATED"
            ? "Sus umbrales son provisionales: el orden dentro del grupo es válido, pero las cifras absolutas cambiarán cuando el perfil se calibre. "
            : "") +
          noComparable
        ).trimEnd(),
        vehicles: sorted,
        // Tasa agregada, no promedio de tasas: un vehículo de 20 km no puede
        // pesar lo mismo que uno de 2.000 (misma regla que las cohortes).
        groupEpisodesPer100Km: per100Km(totalEpisodes, totalKm),
        groupDistanceKm: round(totalKm),
      };
    });

  const warnings: string[] = [];
  if (groups.length > 1) {
    /*
     * El aviso tiene que nombrar la causa CORRECTA de la fragmentación, porque
     * de la causa depende quién tiene que hacer algo:
     *
     *  - cadencias distintas → se corrige configurando los equipos;
     *  - alcance por vehículo → no hay nada que corregir en los equipos. Es la
     *    consecuencia buscada de haber calibrado a cada uno contra su propia
     *    distribución, y lo que falta para poder compararlos es normalizar por
     *    percentil, que es trabajo del análisis y no del taller.
     *
     * El aviso decía siempre lo primero. Con siete equipos de 10 s calibrados
     * uno a uno mandaba a revisar la configuración de siete equipos que estaban
     * bien, y además cada uno quedaba solo en su grupo saliendo «1 de 1»: una
     * posición que parece un ranking y no lo es. Eso último se dice aquí porque
     * un «#1» sin contra quién es justo la cifra que alguien va a citar.
     */
    warnings.push(
      (`La flota se analizó con ${groups.length} perfiles distintos, y cada tabla se ordena solo por dentro; entre tablas las cifras no son comparables. ` +
        (splitByCadence && splitByScope
          ? "Se parte por dos motivos a la vez: hay equipos que no reportan a la misma cadencia —eso se corrige configurando el equipo— y hay vehículos con perfil propio, calibrado contra su propia distribución. "
          : splitByCadence
            ? "Se parte porque los equipos no reportan a la misma cadencia, y a distinta cadencia se mide una magnitud distinta. Eso se corrige configurando el equipo. "
            : "Se parte porque hay vehículos con perfil propio, calibrado contra su propia distribución: todos reportan a la misma cadencia y no hay nada que revisar en la configuración de esos equipos. ") +
        (splitByScope
          ? "Un vehículo con perfil propio queda solo en su tabla, así que su «1 de 1» no es una posición en la flota: para ordenarlos entre sí habría que normalizar por percentil contra la distribución de cada uno."
          : "")
      ).trimEnd(),
    );
  }
  const blinded = excluded.filter((v) => v.reason === "DETECTORS_BLINDED").length;
  if (blinded > 0) {
    warnings.push(
      `${blinded} vehículo(s) quedaron fuera del ranking porque su cadencia de reporte deja ciegos a los detectores. ` +
        "Se corrige configurando el equipo, no el análisis.",
    );
  }
  const unmeasured = excluded.filter((v) => v.reason === "UNMEASURED_FAMILIES").length;
  if (unmeasured > 0) {
    warnings.push(
      `${unmeasured} vehículo(s) quedaron fuera del ranking porque alguna familia no tuvo ni una oportunidad de dispararse: ` +
        "su umbral quedó por encima de todo lo que ese vehículo hizo en el período. " +
        "Aquí la cadencia alcanza; lo que falta es calibrar ese vehículo contra su propia distribución.",
    );
  }
  if (groups.some((group) => group.calibration === "UNCALIBRATED")) {
    warnings.push(
      "Hay grupos con perfiles sin calibrar. El orden relativo ya sirve para priorizar; las cifras absolutas, todavía no.",
    );
  }
  const parked = groups
    .flatMap((group) => group.vehicles)
    .filter((v) => v.averageSpeedKmh !== null && v.averageSpeedKmh < 5).length;
  if (parked > 0) {
    warnings.push(
      `${parked} vehículo(s) promedian menos de 5 km/h en el período: el equipo siguió reportando parqueado. ` +
        "Por eso la tabla usa tasas por 100 km y no por hora.",
    );
  }

  return {
    version: FLEET_COMPARISON_VERSION,
    minimumDistanceKm,
    analysed: input.rows.length,
    ranked: eligible.length,
    groups,
    // Orden fijo también en los excluidos, por el mismo motivo.
    excluded: excluded.sort((a, b) => (a.deviceId < b.deviceId ? -1 : a.deviceId > b.deviceId ? 1 : 0)),
    warnings,
  };
}
